import { create } from "zustand";
import axios from "axios-base";
import { useCartStore } from "context/cartStore";

let interceptorsAttached = false;
let refreshPromise = null;

const SESSION_HINT_KEY = "webr:auth:hasSession";
const getSessionHint = () => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(SESSION_HINT_KEY) === "true";
};
const setSessionHint = (value) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(SESSION_HINT_KEY, value ? "true" : "false");
};

const shouldSkipRefresh = (url = "") => {
  const value = String(url).toLowerCase();
  return (
    value.includes("users/login") ||
    value.includes("users/logout") ||
    value.includes("users/register") ||
    value.includes("users/refresh") ||
    value.includes("users/me") ||
    value.includes("users/forget-password") ||
    value.includes("users/reset-password") ||
    value.includes("users/change-password") ||
    value.includes("auth/password-otp") ||
    value.includes("password-otp/send") ||
    value.includes("password-otp/verify") ||
    value.includes("password-otp/reset")
  );
};

const attachInterceptors = (store) => {
  if (interceptorsAttached) return;
  interceptorsAttached = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const originalRequest = error?.config;

      if (!originalRequest || status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      const urlToCheck = originalRequest.url;
      if (shouldSkipRefresh(urlToCheck)) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      const ok = await store.getState().refreshSession();
      if (!ok) {
        return Promise.reject(error);
      }

      return axios(originalRequest);
    },
  );
};

const doRefresh = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axios
    .post("users/refresh")
    .then(() => true)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

let refreshInterval = null;

export const useAuthStore = create((set, get) => {
  attachInterceptors({ getState: () => get() });

  return {
    user: null,
    loading: false,
    initialized: false,
    isRedirect: false,

    setRedirect: (value) => set({ isRedirect: value }),
    setUser: (user) => set({ user }),

    startAutoRefresh: () => {
      if (refreshInterval) return;
      // Refresh every 1 day
      refreshInterval = setInterval(
        async () => {
          const user = get().user;
          if (user && getSessionHint()) {
            await get().refreshSession();
          }
        },
        24 * 60 * 60 * 1000,
      );
    },

    stopAutoRefresh: () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
    },

    fetchMe: async () => {
      try {
        const res = await axios.get("users/me");
        const user = res?.data?.data || res?.data?.user || null;
        set({ user });
        return true;
      } catch (error) {
        set({ user: null });
        return false;
      }
    },

    refreshSession: async () => {
      const ok = await doRefresh();
      if (!ok) set({ user: null });
      return ok;
    },

    initialize: async () => {
      if (get().initialized) {
        return;
      }

      // Skip initialization on auth pages if no session hint
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        const hasHint = getSessionHint();
        if ((path.includes("/auth/") || path === "/auth") && !hasHint) {
          set({ loading: false, initialized: true });
          return;
        }
      }

      set({ loading: true });
      const ok = await get().fetchMe();

      if (!ok) {
        // Only try refresh if session hint existed (means user was logged in before)
        const hadSession = getSessionHint();
        setSessionHint(false); // Clear hint since fetchMe failed

        if (hadSession) {
          const refreshed = await get().refreshSession();
          if (refreshed) {
            await get().fetchMe();
          }
        }
      }

      set({ loading: false, initialized: true });
      if (get().user) {
        get().startAutoRefresh();
      }
    },

    login: async (payload) => {
      set({ loading: true });
      try {
        const res = await axios.post("users/login", payload);
        const user = res?.data?.user || res?.data?.data || null;
        set({ user, initialized: true });
        setSessionHint(true);
        get().startAutoRefresh();

        // Sync guest cart with server after successful login
        if (user) {
          try {
            const cartStore = useCartStore.getState();
            const guestItems = cartStore.items;
            if (guestItems && guestItems.length > 0) {
              await cartStore.syncOnLogin(user);
            }
          } catch (err) {
            // Don't throw - login was successful, cart sync is optional
          }
        }

        return true;
      } catch (error) {
        set({ user: null });
        setSessionHint(false);
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    register: async (payload) => {
      set({ loading: true });
      try {
        await axios.post("users/register", payload);
        set({ isRedirect: true });
        return true;
      } finally {
        set({ loading: false });
      }
    },

    logout: async () => {
      get().stopAutoRefresh();
      try {
        await axios.post("users/logout");
      } catch {}

      // Clear all storage
      if (typeof window !== "undefined") {
        try {
          sessionStorage.removeItem("checkout-storage");
          localStorage.removeItem("cart-storage");
          localStorage.removeItem("wishlist-storage");

          // Reset Zustand stores by triggering storage event
          // This forces stores to reinitialize from empty storage
          window.dispatchEvent(new Event("storage"));
        } catch {}
      }

      set({ user: null });
      setSessionHint(false);
    },
  };
});
